import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import { Button, Modal, Select, InputNumber, Input, Spin, Empty } from "antd";
import { toast } from "../toasts/toastStore";
import { apiGeneralInstance, authHeaderGeneral } from "../../redux/actions/general/general";

const TIPO_LABEL = { conteo: "Conteo fisico", merma: "Merma", rotura: "Rotura", perdida: "Perdida" };
const num = (v) => parseFloat(v).toLocaleString("es-MX", { maximumFractionDigits: 3 });

export default function AjustesInventario({ producto, onChanged }) {
  const idProducto = producto.id_producto;
  const variantes = producto.variantes || [];
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchItems = useCallback(() => {
    apiGeneralInstance.get(`/ajustes/producto/${idProducto}`, { headers: authHeaderGeneral() })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("No se pudieron cargar los ajustes"))
      .finally(() => setLoading(false));
  }, [idProducto]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    apiGeneralInstance.get("/ajustes/tipos", { headers: authHeaderGeneral() })
      .then((r) => setTipos(r.data?.items || []))
      .catch(() => {});
  }, []);

  const varianteSel = variantes.find((v) => v.id_variante === form.id_variante);
  const nombreVariante = (v) => `${v.clave || "Variante"}${v.descripcion_variante ? ` - ${v.descripcion_variante}` : ""} (existencia: ${num(v.existencia)})`;

  const handleSave = async () => {
    if (!form.id_variante) return toast.warning("Selecciona la variante");
    if (!form.id_tipo_ajuste) return toast.warning("Selecciona el tipo de ajuste");
    if (form.existencia_nueva == null) return toast.warning("Indica la existencia nueva");
    setSaving(true);
    try {
      const res = await apiGeneralInstance.post(`/ajustes/producto/${idProducto}`, form, { headers: authHeaderGeneral() });
      setItems(res.data?.items || []);
      toast.success("Ajuste registrado");
      setOpen(false);
      setForm({});
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el ajuste");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = (a) => {
    let motivo = "";
    Modal.confirm({
      title: "Cancelar ajuste",
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>Se regresara la existencia al valor previo al ajuste.</div>
          <Input.TextArea rows={2} placeholder="Motivo de la cancelacion" onChange={(e) => { motivo = e.target.value; }} />
        </div>
      ),
      okText: "Cancelar ajuste",
      okButtonProps: { danger: true },
      cancelText: "Volver",
      onOk: async () => {
        if (!motivo.trim()) { toast.warning("El motivo es obligatorio"); throw new Error("motivo"); }
        try {
          const res = await apiGeneralInstance.post(`/ajustes/${a.id_ajuste}/cancelar`, { motivo }, { headers: authHeaderGeneral(), params: { id_producto: idProducto } });
          setItems(res.data?.items || []);
          toast.success("Ajuste cancelado");
          onChanged && onChanged();
        } catch (err) {
          if (err.message !== "motivo") toast.error(err?.response?.data?.detail || "No se pudo cancelar");
          throw err;
        }
      },
    });
  };

  return (
    <div className="cd-card">
      <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="cd-card-icon-wrap"><i className="fa-solid fa-boxes-stacked"></i></div>
          <h2 className="cd-card-title">Ajustes de inventario</h2>
          <span className="cd-actividad-count">{items.length}</span>
        </div>
        {variantes.length > 0 && (
          <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => { setForm({}); setOpen(true); }}
            style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Nuevo ajuste</Button>
        )}
      </div>

      {loading ? <Spin /> : items.length === 0 ? <Empty description="Sin ajustes" /> : (
        <div>
          {items.map((a) => (
            <div key={a.id_ajuste} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1.5fr 1fr auto", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2", opacity: a.cancelado ? 0.55 : 1 }}>
              <div>
                <span className="cd-field-label">Variante</span>
                <div className="cd-field-value">{a.clave}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{TIPO_LABEL[a.tipo] || a.tipo}</div>
              </div>
              <div>
                <span className="cd-field-label">Existencia</span>
                <div className="cd-field-value">{num(a.existencia_anterior)} → {num(a.existencia_nueva)}</div>
              </div>
              <div>
                <span className="cd-field-label">Motivo</span>
                <div className="cd-field-value">{a.motivo || "—"}</div>
                {a.cancelado ? <div style={{ fontSize: 12, color: "#b91c1c" }}>Cancelado por {a.cancelado_por}: {a.motivo_cancelacion}</div> : null}
              </div>
              <div>
                <span className="cd-field-label">Registro</span>
                <div className="cd-field-value">{a.creado_por}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{dayjs(a.datetime).format("D MMM YYYY, HH:mm")}</div>
              </div>
              <div>
                {!a.cancelado && <Button size="small" danger onClick={() => handleCancelar(a)}>Cancelar</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal title="Nuevo ajuste de inventario" open={open} onCancel={() => setOpen(false)} onOk={handleSave} confirmLoading={saving} okText="Registrar" cancelText="Cancelar" okButtonProps={{ style: { backgroundColor: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div className="cd-field-label">Variante</div>
            <Select style={{ width: "100%" }} placeholder="Selecciona la variante" value={form.id_variante}
              onChange={(v) => setForm({ ...form, id_variante: v })}
              options={variantes.map((v) => ({ value: v.id_variante, label: nombreVariante(v) }))} />
          </div>
          <div>
            <div className="cd-field-label">Tipo de ajuste</div>
            <Select style={{ width: "100%" }} value={form.id_tipo_ajuste} onChange={(v) => setForm({ ...form, id_tipo_ajuste: v })}
              options={tipos.map((t) => ({ value: t.id, label: TIPO_LABEL[t.nombre] || t.nombre }))} />
          </div>
          <div>
            <div className="cd-field-label">Existencia nueva{varianteSel ? ` (actual: ${num(varianteSel.existencia)})` : ""}</div>
            <InputNumber style={{ width: "100%" }} min={0} value={form.existencia_nueva} onChange={(v) => setForm({ ...form, existencia_nueva: v })} />
          </div>
          <div>
            <div className="cd-field-label">Motivo (opcional)</div>
            <Input.TextArea rows={2} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
